import { inject, Injectable } from '@angular/core';
import { AccessRightType, ApplicationSettingsType, ObjectTypes, SideListOptionType } from '../shared/types';
import { AccessToApplicationTypeService } from '../shared/fi-shared-services/access-to-application-type.service';
import { AppTextService } from '../shared/app-text';
import { FiOnboardingService } from './fi-onboarding-builder/fi-onboarding.service';
import { FiStateService } from '../shared/fi-state-proxy/fi-state.proxy.service';
import { FiCustomerDataService } from '../shared/fi-shared-services/fi-customer-data.service';
import { ITabItem } from '../utilities/tabs/tabs';
import { FiStateNames } from '../shared/fi-state-proxy/fi-state-names';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { isDefined } from '@fi-app/shared/utils';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiUserSettingType } from '@fi-shared/types/fi-user-setting-type';
import { FiOnboardingActiveDataService } from '@fi-app/fi-onboarding/fi-onboarding-active/fi-onboarding-active-data.service';

@Injectable()
export class FiOnboardingNavigationService {
    private readonly accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private readonly appTextService = inject(AppTextService);
    private readonly fiOnboardingService = inject(FiOnboardingService);
    private readonly fiCustomerDataService = inject(FiCustomerDataService);
    private readonly fiStateService = inject(FiStateService);
    private readonly fiLoading = inject(LoadingService);
    private readonly fiUserDataService = inject(FiUserDataService);
    private readonly fiOnboardingActiveDataService = inject(FiOnboardingActiveDataService);

    private static FI_MAX_ONBOARDING_MODULE_VISITS = 5;

    constructor() {}

    getOnboardingTabs(): ITabItem[] {
        const tabs: ITabItem[] = [];
        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.OnboardingLocal,
                AccessRightType.Read,
            )
        ) {
            const hasShareRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.OnboardingLocal,
                AccessRightType.Share,
            );
            const hasAdminRights = this.getHasAdminRights();

            tabs.push({
                Title: this.appTextService.get('onboarding.my_onboardings'),
                Sref: FiStateNames.Onboarding.Active,
                //SrefParams: {},
            });

            if (hasAdminRights) {
                tabs.push({
                    Title: this.appTextService.get('onboarding.onboarding_guide'),
                    Sref: FiStateNames.Onboarding.Guide,
                });
            }

            if (hasAdminRights) {
                tabs.push({
                    Title: this.appTextService.get('onboarding.quizzes'),
                    Sref: FiStateNames.Onboarding.Quiz.List,
                    SrefParams: { filterType: SideListOptionType.SharedWithMe },
                    AddState: hasShareRights ? FiStateNames.Onboarding.Quiz.Add : undefined,
                });

                tabs.push({
                    Title: this.appTextService.get('onboarding.todo_lists'),
                    Sref: FiStateNames.Onboarding.Todo.List,
                    SrefParams: { filterType: SideListOptionType.SharedWithMe },
                    AddState: hasShareRights ? FiStateNames.Onboarding.Todo.Add._name : undefined,
                });
            }

            if (hasShareRights) {
                tabs.push({
                    Title: this.appTextService.get('onboarding.onboarding_builder'),
                    Sref: FiStateNames.Onboarding.List,
                    SrefParams: { filterType: SideListOptionType.SharedWithMe },
                    AddState: hasShareRights ? FiStateNames.Onboarding.Add : undefined,
                });
            }
        }

        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.OnboardingReports,
                AccessRightType.Read,
            )
        ) {
            tabs.push({
                Title: this.appTextService.get('onboarding.reports'),
                Sref: FiStateNames.Onboarding.Report.Dashboard,
                AddState: FiStateNames.Onboarding.Report.Dashboard,
            });
        }
        return tabs;
    }

    private getHasAdminRights() {
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.OnboardingLocal,
            AccessRightType.Admin,
        );
    }

    onOnboardingMenuItemClick() {
        const tabs = this.getOnboardingTabs();
        const userSettings = this.fiUserDataService.getUserData().UserSettings;
        const onboardingVisitsCount: number = (userSettings[FiUserSettingType.OnboardingVisitsCount] as number) ?? 0;
        const promise = new Promise<ITabItem>((resolve, reject) => {
            if (
                !this.getHasAdminRights() ||
                onboardingVisitsCount > FiOnboardingNavigationService.FI_MAX_ONBOARDING_MODULE_VISITS ||
                this.fiOnboardingActiveDataService.onboardingActiveData().ActiveOnboardings.length
            ) {
                // no admin rights or there are active onboardings or user already has more than FI_MAX_ONBOARDING_MODULE_VISITS visits
                // to onboarding module, just go to my onboardings
                resolve(tabs[0]);
            } else {
                const guideTab = tabs.find((tab) => tab.Sref === FiStateNames.Onboarding.Guide);
                resolve(guideTab ?? tabs[0]);
            }
        });
        this.fiLoading.trackPromise(promise).then((tabToGo) => {
            if (isDefined(tabToGo)) {
                if (onboardingVisitsCount <= FiOnboardingNavigationService.FI_MAX_ONBOARDING_MODULE_VISITS) {
                    const newOnboardingVisitsCount = onboardingVisitsCount + 1;
                    this.fiOnboardingService.setOnboardingVisitsCount(newOnboardingVisitsCount).then((result) => {
                        userSettings[FiUserSettingType.OnboardingVisitsCount] = result;
                    });
                }
                this.fiStateService.go(tabToGo.Sref);
            }
        });
    }
}
